import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';

const AuthGateway = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [userType, setUserType] = useState('customer');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    company: ''
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const userTypeOptions = [
    { value: 'customer', label: 'Customer' },
    { value: 'admin', label: 'Administrator' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (activeTab === 'register') {
      if (!formData?.name) {
        newErrors.name = 'Name is required';
      }
      
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Auth attempt:', { 
        type: activeTab, 
        userType, 
        email: formData?.email 
      });

      // Simulate successful authentication
      const userData = {
        email: formData?.email,
        name: formData?.name || 'User',
        type: userType
      };

      // Store user data (in real app, this would be handled by auth context)
      localStorage.setItem('user', JSON.stringify(userData));

      // Redirect based on user type
      if (userType === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/customer-portal');
      }

      onClose();
    } catch (error) {
      console.error('Authentication error:', error);
      setErrors({ general: 'Authentication failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`${provider} login attempted`);
    // Implement social login logic
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      phone: '',
      company: ''
    });
    setErrors({});
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Sparkles" size={20} color="white" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary">
              Clean360
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close authentication form"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-border">
          <button
            onClick={() => switchTab('login')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'login' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Sign In
          </button>
          <button
            onClick={() => switchTab('register')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'register' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Sign Up
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* User Type Selection */}
          <div className="mb-6">
            <Select
              label="Account Type"
              options={userTypeOptions}
              value={userType}
              onChange={setUserType}
              className="mb-4"
            />
          </div>

          {/* General Error */}
          {errors?.general && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                <span className="text-sm text-error">{errors?.general}</span>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {activeTab === 'register' && (
              <>
                <Input
                  label="Full Name"
                  type="text"
                  required
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  error={errors?.name}
                  placeholder="Enter your full name"
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  placeholder="Enter your phone number"
                />

                {userType === 'admin' && (
                  <Input
                    label="Company"
                    type="text"
                    value={formData?.company}
                    onChange={(e) => handleInputChange('company', e?.target?.value)}
                    error={errors?.company}
                    placeholder="Enter company name"
                  />
                )}
              </>
            )}

            <Input
              label="Email Address"
              type="email"
              required
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              placeholder="Enter your email"
            />

            <Input
              label="Password"
              type="password"
              required
              value={formData?.password}
              onChange={(e) => handleInputChange('password', e?.target?.value)}
              error={errors?.password}
              placeholder="Enter your password"
            />

            {activeTab === 'register' && (
              <Input
                label="Confirm Password"
                type="password"
                required
                value={formData?.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                error={errors?.confirmPassword}
                placeholder="Confirm your password"
              />
            )}

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              className="mt-6"
            >
              {activeTab === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {/* Social Login */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-text-secondary">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSocialLogin('google')}
                iconName="Chrome"
                iconPosition="left"
                disabled={isLoading}
              >
                Google
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSocialLogin('facebook')}
                iconName="Facebook"
                iconPosition="left"
                disabled={isLoading}
              >
                Facebook
              </Button>
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-6 text-center">
            {activeTab === 'login' ? (
              <div className="space-y-2">
                <button
                  type="button"
                  className="text-sm text-primary hover:underline"
                  onClick={() => console.log('Forgot password clicked')}
                >
                  Forgot your password?
                </button>
                <div className="text-sm text-text-secondary">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchTab('register')}
                    className="text-primary hover:underline font-medium"
                  >
                    Sign up
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-text-secondary">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchTab('login')}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </button>
              </div>
            )}
          </div>

          {/* Terms */}
          {activeTab === 'register' && (
            <div className="mt-4 text-xs text-text-secondary text-center">
              By creating an account, you agree to our{' '}
              <a href="#" className="text-primary hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthGateway;